import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CompaniesApi from '../../../../api/companies';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('he-IL'));

const SERIES = [
  { key: 'phonebookCount', label: 'סה"כ אלפון', color: '#4299E1' },
  { key: 'activeEmployees', label: 'פעילים (רשומים)', color: '#38B2AC' },
  { key: 'activeUnregistered', label: 'פעילים לא רשומים', color: '#F6AD55' },
  { key: 'registeredEmployees', label: 'רשומים', color: '#9F7AEA' },
  { key: 'activelyTransferring', label: 'ביצעו העברה', color: '#01B574' },
];

export default function EmployeeStatusDrilldownModal({ isOpen, onClose, company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  useEffect(() => {
    if (!isOpen || !company?.companyId) {
      setData(null);
      return;
    }
    setLoading(true);
    CompaniesApi.getStatsForCompany(company.companyId, 90)
      .then((res) => setData(res?.data ?? res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isOpen, company?.companyId]);

  const current = data?.current ?? company;
  const history = data?.history ?? [];

  const funnelSegments = useMemo(() => {
    if (!current) return [];
    const total = Math.max(current.phonebookCount ?? 0, current.activeEmployees ?? 0, 1);
    const transferring = current.activelyTransferring ?? 0;
    const registered = Math.max((current.registeredEmployees ?? 0) - transferring, 0);
    const activeRegistered = Math.max((current.activeEmployees ?? 0) - (current.registeredEmployees ?? 0), 0);
    const activeUnregistered = current.activeUnregistered ?? 0;
    const dormant = Math.max(total - transferring - registered - activeRegistered - activeUnregistered, 0);
    return [
      { key: 'transferring', label: 'ביצעו העברה', value: transferring, color: '#01B574' },
      { key: 'registered', label: 'רשומים (לא העבירו)', value: registered, color: '#9F7AEA' },
      { key: 'activeUnregistered', label: 'פעילים לא רשומים (הזדמנות)', value: activeUnregistered, color: '#F6AD55' },
      { key: 'dormant', label: 'באלפון, לא פעילים', value: dormant, color: '#CBD5E0' },
    ].filter((s) => s.value > 0);
  }, [current]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          סטטוס עובדים — {current?.companyName}{' '}
          <Text as="span" fontSize="sm" color="gray.500">
            #{current?.companyId}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : !current ? (
            <Text color="gray.500">אין נתונים</Text>
          ) : (
            <>
              <Flex gap={3} wrap="wrap" mb={4}>
                <Stat flex={1} minW="150px" bg={panelBg} p={3} borderRadius="md">
                  <StatLabel>סה"כ באלפון</StatLabel>
                  <StatNumber>{num(current.phonebookCount)}</StatNumber>
                  <StatHelpText>מאלפון מוצפן</StatHelpText>
                </Stat>
                <Stat flex={1} minW="150px" bg={panelBg} p={3} borderRadius="md">
                  <StatLabel>פעילים רשומים</StatLabel>
                  <StatNumber>{num(current.activeEmployees)}</StatNumber>
                  <StatHelpText>משמרת ב-60 יום</StatHelpText>
                </Stat>
                <Stat flex={1} minW="150px" bg={panelBg} p={3} borderRadius="md">
                  <StatLabel>פעילים לא רשומים</StatLabel>
                  <StatNumber color="orange.500">{num(current.activeUnregistered)}</StatNumber>
                  <StatHelpText>הזדמנות אונבורדינג</StatHelpText>
                </Stat>
                <Stat flex={1} minW="150px" bg={panelBg} p={3} borderRadius="md">
                  <StatLabel>רשומים</StatLabel>
                  <StatNumber>{num(current.registeredEmployees)}</StatNumber>
                </Stat>
                <Stat flex={1} minW="150px" bg={panelBg} p={3} borderRadius="md">
                  <StatLabel>ביצעו העברה</StatLabel>
                  <StatNumber color="green.500">{num(current.activelyTransferring)}</StatNumber>
                </Stat>
              </Flex>

              {funnelSegments.length > 0 && (
                <Box mb={5}>
                  <Text fontSize="sm" mb={2} color="gray.600">
                    פאנל (יחסית ל-{num(Math.max(current.phonebookCount ?? 0, current.activeEmployees ?? 0))} עובדים)
                  </Text>
                  <Flex h="24px" borderRadius="md" overflow="hidden" borderWidth={1}>
                    {(() => {
                      const total = funnelSegments.reduce((a, s) => a + s.value, 0);
                      return funnelSegments.map((s) => (
                        <Box
                          key={s.key}
                          bg={s.color}
                          flex={s.value / Math.max(total, 1)}
                          title={`${s.label}: ${num(s.value)}`}
                        />
                      ));
                    })()}
                  </Flex>
                  <Flex gap={4} mt={2} wrap="wrap">
                    {funnelSegments.map((s) => (
                      <Flex key={s.key} align="center" gap={1}>
                        <Box w="10px" h="10px" borderRadius="sm" bg={s.color} />
                        <Text fontSize="xs">
                          {s.label}: {num(s.value)}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              )}

              <Text fontSize="sm" mb={2} color="gray.600">
                היסטוריית 90 יום
              </Text>
              {history.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  אין היסטוריה עדיין — תתמלא עם הרצת הקרון הלילית הראשונה.
                </Text>
              ) : (
                <Box h="280px">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="snapshotDate" fontSize={11} />
                      <YAxis fontSize={11} />
                      <RTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {SERIES.map((s) => (
                        <Area
                          key={s.key}
                          type="monotone"
                          dataKey={s.key}
                          name={s.label}
                          stroke={s.color}
                          fill={s.color}
                          fillOpacity={0.15}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Text fontSize="xs" color="gray.500">
            המטריקות מתעדכנות אוטומטית מדי לילה בשעה 03:30 (שעון ישראל).
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
